import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import {useNavigate, useLocation} from "react-router-dom";
import {
  FormEvent,
  useEffect,
  useState
} from "react";
import { Box, Paper, Typography, Grid, TextField, Button, useTheme, Divider, Fade } from '@mui/material';
import ErrorDialog from "../../../components/ErrorDialog";
import AuthLayout from '../layouts/AuthLayout/AuthLayout';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { googleLogin } from '../../../App';

function Register() {
  let navigate = useNavigate();
  let location = useLocation();
  let auth = useAuthContext();
  let theme = useTheme();

  const minPasswordLength = 8;
  const hasUpperCase = /[A-Z]/;
  const hasNumber = /[0-9]/;
  const hasSpecialChar = /[\^$*.[\]{}()?"!@#%&,/\\><':;|_~`+=-]/;

  const [password, setPassword] = useState<string>("");
  const [confirmedPassword, setConfirmedPassword] = useState<string>("");

  const [showValidationText, setShowValidationText] = useState<boolean>(false);
  const [showMatchingText, setShowMatchingText] = useState<boolean>(false);

  const [text1, setText1] = useState<boolean>(false);
  const [text2, setText2] = useState<boolean>(false);
  const [text3, setText3] = useState<boolean>(false);

  const [matchingPasswords, setMatchingPasswords] = useState<boolean>(false);

  const [error, setError] = useState<string>("");

  useEffect(() => {
    setText1(password.length >= minPasswordLength);
    setText2(hasUpperCase.test(password));
    setText3(hasNumber.test(password) && (hasSpecialChar.test(password)));
    setMatchingPasswords(password === confirmedPassword);

  }, [password, confirmedPassword]);

  const handleActiveSignUp = () => {
    return !(text1 && text2 && text3 && matchingPasswords);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleConfirmedPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmedPassword(event.target.value);
  };

  function validatePassword(password: string): string | null {

    if (password.length < minPasswordLength) {
        return `Password must be at least ${minPasswordLength} characters long.`;
    }
    if (!hasUpperCase.test(password)) {
        return 'Password must include at least one uppercase letter.';
    }
    if (!hasNumber.test(password) || (!hasSpecialChar.test(password))) {
        return 'Password must include at least one special character and one number.';
    }
    return null;
}

  function handleSubmit(e: FormEvent<HTMLFormElement>){
    e.preventDefault();

    let formData = new FormData(e.currentTarget);
    let username = formData.get("username") as string;
    let password = formData.get("password") as string;
    let confirmedPassword = formData.get("confirmedPassword") as string;

    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      setError(passwordValidationError);
      return;
    }

    if (password != confirmedPassword) {
      setError("Passwords do not match.");
      return;
    }

    auth.signup(username, password, (result: any) => {
        // if user had registered but isn't confirmed, show the confirmation form
        if(result.success) {
          if (result.data.userConfirmed === false) {
            navigate('/activate', { state: { username: username } });
          }
          if (result.data.userConfirmed === true) {
            navigate('/beacons');
          }
        }
        if(!result.success) {
          if ( result.data.err && result.data.err.code === "UsernameExistsException") {
            navigate('/activate', { state: { username: username } });
          } 
          else if ( result.data.err && result.data.err.code === "InvalidParameterException") {
            setError("Invalid email or password.");
          } else {
            setError(result.data.err.message);
          }
        }
      });
    }

  function onCloseErrorDialog() {
    setError("");
}

  return (
    <AuthLayout>
        <Box maxWidth="sm" mx="auto" mt={10} borderRadius={2} boxShadow={3}>
          <Box padding={4}>
          <Typography variant="h5" component="h2" align="center" mb={8} fontWeight="600">Sign Up</Typography>
          <form onSubmit={handleSubmit}>
            <Grid container gap={4}>
              <Grid item xs={12}>
                <TextField name='username' label='Email' fullWidth autoFocus />
              </Grid>
              <Grid item xs={12}>
                <TextField name='password' label='Password' fullWidth type="password" onChange={handlePasswordChange} onFocus={() => setShowValidationText(true)} onBlur={() => setShowValidationText(false)}/>
              </Grid>
              <Grid item xs={12}>
                <TextField name='confirmedPassword' label='Confirm Password' fullWidth type="password" onChange={handleConfirmedPasswordChange} onFocus={() => setShowMatchingText(true)} onBlur={() => setShowMatchingText(false)}/>
              </Grid>
              <Grid item xs={0}>
              {showValidationText && password.length > 0 && (
                <Fade in={true} timeout={1000}>
                <Box sx={{ display: "block", ml: 3}}>
                  {text1 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckIcon sx={{ color: "green", fontSize: 14 }} /> <Typography sx={{ color: "green", fontSize: 14 }}>At least 8 characters long</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ClearIcon sx={{ color: "red", fontSize: 14 }}/> <Typography sx={{ color: "red", fontSize: 14 }}>At least 8 characters long</Typography>
                  </Box>
                  )}
                  {text2 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon sx={{ color: "green", fontSize: 14 }}/> <Typography sx={{ color: "green", fontSize: 14 }}>At least 1 uppercase letter</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ClearIcon sx={{ color: "red", fontSize: 14 }}/> <Typography sx={{ color: "red", fontSize: 14 }}>At least 1 uppercase letter</Typography>
                    </Box>
                  )}
                  {text3 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon sx={{ color: "green", fontSize: 14 }}/> <Typography sx={{ color: "green", fontSize: 14 }}>At least 1 special character and number</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ClearIcon sx={{ color: "red", fontSize: 14 }}/> <Typography sx={{ color: "red", fontSize: 14 }}>At least 1 special character and number</Typography>
                    </Box>
                  )}
                </Box>
                </Fade>
                )}
                {showMatchingText && confirmedPassword.length > 0 && (
                <Fade in={true} timeout={1000}>
                <Box sx={{ display: "block", ml: 3}}>
                    {matchingPasswords ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckIcon sx={{ color: "green", fontSize: 14 }}/> <Typography sx={{ color: "green", fontSize: 14 }}>Passwords match</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ClearIcon sx={{ color: "red", fontSize: 14 }}/> <Typography sx={{ color: "red", fontSize: 14 }}>Passwords match</Typography>
                      </Box>
                    )}
                </Box>
                </Fade>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Button type="submit" variant="contained" size="large" fullWidth disabled={handleActiveSignUp()}>Sign Up</Button> 
              </Grid>
            </Grid>
          </form>

          <Grid container mt={4} color={theme.palette.grey['600']}>
            <Grid item xs={12} textAlign="center">
              <Typography variant="body2" mt={2}>Already registered? <Link to="/login">Log in</Link></Typography>
            </Grid>
          </Grid>
          <Box display="flex" alignItems="center" mt={4}>
              <Divider sx={{ flexGrow: 1 }} />
              <Typography variant="body2" mx={2}>or</Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>
    
            <Box display="flex" flexDirection="column" mt={2}>
              <Button variant="text" size="large" fullWidth onClick={googleLogin}>
                Sign in with SSO
              </Button>
            </Box>
        </Box>
      </Box>

      <ErrorDialog errorMessage={error} open={error.length > 0} onClose={onCloseErrorDialog} />
    </AuthLayout>
  );
}

export default Register;